import React, {
  Fragment,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { VList } from 'virtua';

interface FilterOption {
  label: string;
  value: string;
  hint?: string;
}

interface FilterProps {
  options: FilterOption[];
  onChange: (value: string) => void;
  buttonLabel?: string;
  icon?: ReactNode;
  value?: string | null;
  hasTriggerIcon?: boolean;
  className?: string;
  name?: string;
  label?: ReactNode;
  containerClassName?: string;
  required?: boolean;
  contentClassName?: string;
  menuItemClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  isVirtualizedList?: boolean;
  maxItems?: number;
  onClose?: () => void;
  disabled?: boolean;
}

const CustomSelect = ({
  options,
  onChange,
  buttonLabel = 'Select',
  icon,
  value,
  hasTriggerIcon = true,
  className,
  label,
  containerClassName,
  required,
  contentClassName,
  menuItemClassName,
  searchable = false,
  searchPlaceholder = 'Search...',
  maxItems = 100,
  isVirtualizedList,
  onClose,
  disabled = false,
}: FilterProps) => {
  const selectedLabel = value
    ? options.find((item) => item.value === value)?.label
    : buttonLabel;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [contentWidth, setContentWidth] = useState<number | undefined>();

  const filteredOptions = useMemo(() => {
    if (!searchable || !query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q)
    );
  }, [options, searchable, query]);

  useLayoutEffect(() => {
    if (open) {
      if (triggerRef.current) {
        setContentWidth(triggerRef.current.offsetWidth);
      }

      if (searchable) {
        const timeoutId = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [open, searchable]);

  return (
    <div className={containerClassName}>
      <div className={cn(' flex items-start gap-1')}>
        {label && (
          <p className='mb-1 leading-[100%] text-sm font-medium'>{label}</p>
        )}
        {required && (
          <span>
            <svg
              width='6'
              height='6'
              viewBox='0 0 6 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M2.21732 5.72728L2.31178 3.62927L0.541892 4.76776L-1.11163e-05 3.82316L1.87925 2.86364L-1.11163e-05 1.90412L0.541892 0.959522L2.31178 2.09802L2.21732 5.24521e-06H3.29615L3.20169 2.09802L4.97158 0.959522L5.51348 1.90412L3.63422 2.86364L5.51348 3.82316L4.97158 4.76776L3.20169 3.62927L3.29615 5.72728H2.21732Z'
                fill='#EE4545'
              />
            </svg>
          </span>
        )}
      </div>

      <div className='bg-transparent flex flex-row justify-center'>
        <DropdownMenu
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              onClose?.();
            }
          }}
        >
          <DropdownMenuTrigger asChild className={cn('h-10.5')}>
            <Button
              variant='outline'
              className={cn(
                'flex items-center gap-2 px-4 py-2 bg-white text-foreground font-medium border border-solid border-[#b1aa9f] focus:border rounded-[8px] text-sm h-10.5 text-left w-full justify-between',
                className
              )}
              ref={triggerRef}
              disabled={disabled}
            >
              <span
                className={cn(
                  'text-wrap leading-[100%]',
                  selectedLabel === buttonLabel &&
                    'text-muted-foreground font-normal'
                )}
              >
                {selectedLabel}
              </span>
              {hasTriggerIcon && (
                <Fragment>
                  {icon || (
                    <ChevronDown size={24} className='w-6 h-6' />
                    // <Image
                    //   src={CaretDown}
                    //   width={24}
                    //   height={24}
                    //   className='w-6 h-6'
                    //   alt='caret down'
                    // />
                  )}
                </Fragment>
              )}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className={cn(
              'bg-white rounded-[10px] border border-solid border-[#b1aa9f] p-0 h-fit max-h-[20rem] overflow-hidden',
              contentClassName
            )}
            align='start'
            sideOffset={4}
            style={{ width: contentWidth }}
          >
            {searchable && (
              <div className='sticky top-0 z-10 bg-white p-2 border-b border-[#b1aa9f]'>
                <input
                  type='text'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className='w-full h-9 px-3 py-1 text-sm border rounded-[8px] focus:outline-none'
                  ref={inputRef}
                  onKeyDown={(e) => {
                    // Prevent Radix typeahead from stealing focus
                    e.stopPropagation();
                  }}
                />
              </div>
            )}

            <div className='max-h-[16rem] h-fit! overflow-y-auto'>
              {filteredOptions?.length === 0 ? (
                <div className='px-3 py-3 text-sm text-muted-foreground'>
                  No options found.
                </div>
              ) : (
                <>
                  {!isVirtualizedList ? (
                    <div>
                      {filteredOptions
                        .slice(
                          0,
                          searchable ? maxItems : filteredOptions.length
                        )
                        .map((option, index) => (
                          <React.Fragment key={option.value}>
                            <DropdownMenuItem
                              className={cn(
                                'px-3 py-4 focus:bg-transparent hover:bg-gray-100 rounded-[10px] cursor-pointer'
                              )}
                              onClick={() => onChange(option.value)}
                            >
                              <div className={cn('w-full', menuItemClassName)}>
                                <p className='font-medium text-sm'>
                                  {option.label}
                                </p>

                                {option?.hint && <p>{option?.hint}</p>}
                              </div>

                              {value === option.value && (
                                <CheckCircle2
                                  className='w-6 h-6'
                                  fill='green'
                                  color='white'
                                />
                              )}
                            </DropdownMenuItem>

                            {!searchable &&
                              index < filteredOptions.length - 1 && (
                                <DropdownMenuSeparator className='mx-3 my-0' />
                              )}
                          </React.Fragment>
                        ))}
                    </div>
                  ) : (
                    <VList style={{ height: '16rem' }} className=''>
                      {filteredOptions.map((option, index: number) => (
                        <Fragment key={option.value}>
                          <DropdownMenuItem
                            className={cn(
                              'px-3 py-4 focus:bg-transparent hover:bg-gray-100 rounded-[10px] cursor-pointer',
                              menuItemClassName
                            )}
                            onClick={() => {
                              onChange(option.value);
                              setOpen(false);
                            }}
                          >
                            <span className='font-medium text-sm truncate mr-2'>
                              {option.label}
                            </span>

                            {value === option.value && (
                              <CheckCircle2
                                className='w-5 h-5 flex-shrink-0'
                                fill='green'
                                color='white'
                              />
                            )}
                          </DropdownMenuItem>

                          {!searchable &&
                            index < filteredOptions.length - 1 && (
                              <DropdownMenuSeparator className='mx-3 my-0' />
                            )}
                        </Fragment>
                      ))}
                    </VList>
                  )}
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default CustomSelect;
